/**
 * @file src/lib/sseMiddleware.js
 * @description Redux middleware for Server-Sent Events (SSE).
 *
 * Responsibilities:
 *  - Open and maintain SSE connection to the invoice processing endpoint
 *  - Parse incoming events and dispatch to invoiceProgressSlice
 *  - Reconnect automatically with exponential backoff on failure
 *  - Close connection cleanly on logout
 *  - Call saveInvoiceProgress API when an invoice reaches "submitted"
 *    (moved here from the reducer — reducers must be pure functions)
 *
 * Why Redux middleware for SSE (not React Query):
 *  - SSE is a push-based stream — React Query is pull-based (fetch/poll)
 *  - The middleware has access to dispatch() making it the right layer
 *    for translating server events into state updates
 *  - A single persistent connection shared across the app is cleaner
 *    than per-component EventSource instances
 *
 * Rules:
 *  - No React, no JSX
 *  - No hardcoded URLs — all from config/constants
 *  - No console.log in production paths — only dev-mode logging
 */

import { addNewInvoice } from "pages/invoice-processing-queue/invoiceProgressSlice";
import { saveInvoiceProgress } from "api/invoice.api";
import {
  API_BASE_URL,
  API_PREFIX,
  SSE_INVOICE_EVENTS_PATH,
  SSE_MAX_RECONNECT_ATTEMPTS,
  SSE_RECONNECT_BASE_DELAY_MS,
} from "config/constants";

// ---------------------------------------------------------------------------
// SSE URL
// ---------------------------------------------------------------------------

const SSE_URL = `${API_BASE_URL}${API_PREFIX}${SSE_INVOICE_EVENTS_PATH}`;

// ---------------------------------------------------------------------------
// Dev logger — silent in production
// ---------------------------------------------------------------------------

const log = (...args) => {
  if (import.meta.env.DEV) console.log("[SSE]", ...args);
};

const logError = (...args) => {
  if (import.meta.env.DEV) console.error("[SSE]", ...args);
};

// ---------------------------------------------------------------------------
// Middleware
// ---------------------------------------------------------------------------

const sseMiddleware = (store) => {
  let eventSource = null;
  let reconnectAttempts = 0;
  let reconnectTimeoutId = null;

  // --------------------------------------------------------------------------
  // closeConnection — clean teardown
  // --------------------------------------------------------------------------

  const closeConnection = () => {
    if (reconnectTimeoutId) {
      clearTimeout(reconnectTimeoutId);
      reconnectTimeoutId = null;
    }
    if (eventSource) {
      eventSource.close();
      eventSource = null;
      log("Connection closed");
    }
    reconnectAttempts = 0;
  };

  // --------------------------------------------------------------------------
  // scheduleReconnect — exponential backoff
  // --------------------------------------------------------------------------

  const scheduleReconnect = () => {
    if (reconnectAttempts >= SSE_MAX_RECONNECT_ATTEMPTS) {
      logError(
        `Max reconnect attempts (${SSE_MAX_RECONNECT_ATTEMPTS}) reached. Giving up.`,
      );
      return;
    }

    // Exponential backoff: 2s, 4s, 8s, 16s, 32s
    const delay = SSE_RECONNECT_BASE_DELAY_MS * Math.pow(2, reconnectAttempts);
    reconnectAttempts += 1;

    log(`Reconnecting in ${delay}ms (attempt ${reconnectAttempts})`);

    reconnectTimeoutId = setTimeout(() => {
      log("Attempting reconnect...");
      initializeConnection();
    }, delay);
  };

  // --------------------------------------------------------------------------
  // handleSubmitted — save progress to backend when invoice is submitted
  // Moved here from the reducer — reducers must be pure functions.
  // --------------------------------------------------------------------------

  const handleSubmitted = async (invoice) => {
    try {
      await saveInvoiceProgress({
        invoices: [
          {
            invoiceId: invoice.invoiceId,
            invoiceName: invoice.invoiceName,
            batchId: invoice.batchId,
            progress: invoice.progress,
            step: "submitted",
          },
        ],
      });
      log("Progress saved for:", invoice.invoiceName);
    } catch (error) {
      // Non-blocking — log and continue. The beforeunload handler
      // in invoice-processing-queue will attempt another save on page close.
      logError("Failed to save invoice progress:", error.message);
    }
  };

  // --------------------------------------------------------------------------
  // initializeConnection
  // --------------------------------------------------------------------------

  const initializeConnection = () => {
    // Guard — never open two connections
    if (eventSource && eventSource.readyState !== EventSource.CLOSED) {
      log("Connection already open");
      return;
    }

    log("Opening connection to", SSE_URL);
    eventSource = new EventSource(SSE_URL, { withCredentials: true });

    // ── onopen ──────────────────────────────────────────────────────────────
    eventSource.onopen = () => {
      log("Connection established");
      reconnectAttempts = 0; // Reset backoff on successful connection
    };

    // ── onmessage ───────────────────────────────────────────────────────────
    eventSource.onmessage = (event) => {
      let parsed;

      try {
        parsed = JSON.parse(event.data);
      } catch {
        logError("Failed to parse event data:", event.data);
        return;
      }

      // Skip the initial handshake message from the server
      if (parsed.message === "Connected to SSE") {
        log("Handshake acknowledged");
        return;
      }

      const transformedData = {
        invoiceId: parsed.invoiceId,
        invoiceName: parsed.invoiceName,
        batchId: parsed.batchId,
        step: parsed.status,
      };

      log("Event received:", transformedData);

      // Dispatch to Redux — invoiceProgressSlice handles state update
      store.dispatch(addNewInvoice(transformedData));

      // Side effect: save progress when invoice reaches "submitted"
      // Get the updated invoice from state after dispatch
      if (parsed.status === "submitted") {
        const state = store.getState();
        const invoices = state.invoiceReducer?.invoices ?? [];
        const invoice = invoices.find(
          (inv) =>
            inv.batchId === parsed.batchId &&
            inv.invoiceName === parsed.invoiceName,
        );
        if (invoice) {
          handleSubmitted(invoice);
        }
      }
    };

    // ── onerror ─────────────────────────────────────────────────────────────
    eventSource.onerror = (error) => {
      logError("Connection error:", error);

      // ReadyState: 0 = CONNECTING, 1 = OPEN, 2 = CLOSED
      if (eventSource.readyState === EventSource.CLOSED) {
        log("Connection closed by server — scheduling reconnect");
        eventSource = null;
        scheduleReconnect();
      } else if (eventSource.readyState === EventSource.CONNECTING) {
        // Browser is already attempting to reconnect natively —
        // let it try once before we apply our own backoff
        log("Browser reconnecting natively...");
      }
    };
  };

  // Open connection immediately when middleware is initialised
  initializeConnection();

  // --------------------------------------------------------------------------
  // Action interceptor
  // --------------------------------------------------------------------------

  return (next) => (action) => {
    // Close SSE connection on any logout action variant
    const isLogout =
      action.type === "user/logoutUser" ||
      action.type === "logoutUser" ||
      action.type === "auth/logoutUser";

    if (isLogout) {
      log("Logout detected — closing connection");
      closeConnection();
    }

    return next(action);
  };
};

export default sseMiddleware;
